import React from 'react'

import { PaneArea } from '../../../types/shared'
import { Toolbar as ToolbarComponent, ToolbarDivider, ToolbarItem } from '../../components'
import { SpecificChartFunctionality } from '../../constants/common'
import { drawingToolsCreatorsByType, drawingToolsTouchCreatorsByType } from '../../creators/index'
import { ElementStyleDialogContext } from '../../modals/element_style'
import { useModelState } from '../../model-hooks/use-model-state'
import { AutoSaveState } from '../../models/constants'
import PaneModel from '../../models/pane'
import { getToolbarDrawingActions } from './drawingActions'
import { getToolbarDrawingSettings } from './drawingSettings'
import { drawingTools, toolbarDrawingTools } from './drawingTools'
import { DrawingSetting, DrawingTool, IToolBarProps } from './interfaces'

const DISABLED_ITEM_TOOLTIP_LABEL = "Disabled due 'lock/hide' drawings active"

export default function Toolbar({
  isSidebar,
  isHorizontal,
  activeTool,
  chartLayoutModel,
  onActiveToolChange,
  onDrawingActionClick,
  onDrawingSettingClick,
  drawingSettings,
  isAlternativeTheme,
  areToolsDisabled,
}: IToolBarProps) {
  const { autoSaveState } = useModelState(
    React.useMemo(() => chartLayoutModel.drawingsInternalStore(), [chartLayoutModel]),
    { watchProperties: ['autoSaveState'] }
  )
  const [isDrawing, setIsDrawing] = React.useState(false)
  const openElementStyleDialog = React.useContext(ElementStyleDialogContext)

  const availableDrawingSettings = getToolbarDrawingSettings({
    isPreserveDrawingsAndAutosaveAvailable: [
      SpecificChartFunctionality.chartPage,
      SpecificChartFunctionality.quotePage,
    ].includes(chartLayoutModel.specificChartFunctionality),
    isAutoSaveInProgress: autoSaveState === AutoSaveState.Saving,
    isIdea: !!chartLayoutModel.idea?.id,
  })

  const availableDrawingActions = getToolbarDrawingActions({
    isAutoSaveActive: autoSaveState !== AutoSaveState.Off,
    isAddNoteAvailable: chartLayoutModel.specificChartFunctionality === SpecificChartFunctionality.chartPage,
    isIdea: !!chartLayoutModel.idea?.id,
  })

  React.useEffect(() => {
    const onMouseDown = (model: PaneModel, area: PaneArea & { isTouch: boolean }) => {
      const isDrawingModeContinuous = drawingSettings[DrawingSetting.DrawingModeContinuous]
      const hasData = model.chart().quote().close.length > 0
      const hasHiddenDrawings = drawingSettings[DrawingSetting.HideDrawings]
      const tool = drawingTools[activeTool].id

      if (tool === DrawingTool.Mouse || isDrawing || hasHiddenDrawings || !hasData) {
        return
      }

      setIsDrawing(true)
      const ToolClass = area.isTouch ? drawingToolsTouchCreatorsByType[tool] : drawingToolsCreatorsByType[tool]
      const element = model.elements().create({
        instance: new ToolClass(area, {}, model, openElementStyleDialog, () => {
          if (!isDrawingModeContinuous) {
            onActiveToolChange(DrawingTool.Mouse)
          }
          setIsDrawing(false)
        }),
      })
      element.one('destroy', ({ instance }) => {
        if (instance.getIsCreator()) {
          setIsDrawing(false)
        }
      })

      element.trigger('select', element)
    }
    PaneModel.bind('mousedown', onMouseDown)
    return () => {
      PaneModel.unbind('mousedown', onMouseDown)
    }
  }, [activeTool, onActiveToolChange, openElementStyleDialog, drawingSettings, isDrawing])

  return (
    <ToolbarComponent
      direction={isHorizontal && !isSidebar ? 'horizontal' : 'vertical'}
      theme={isAlternativeTheme ? 'alternative' : undefined}
    >
      {toolbarDrawingTools.map((item) => (
        <ToolbarItem
          key={item.defaultItem.id}
          defaultItem={item.defaultItem}
          isActive={activeTool === item.defaultItem.id || item.toolGroup?.some((tool) => tool.id === activeTool)}
          groupItems={item.toolGroup}
          groupTitle={item.toolGroupTitle}
          onChange={onActiveToolChange}
          disabled={item.defaultItem.id !== DrawingTool.Mouse && areToolsDisabled}
          disabledTooltip={DISABLED_ITEM_TOOLTIP_LABEL}
        />
      ))}
      <ToolbarDivider />
      {availableDrawingSettings.map((item) => (
        <ToolbarItem
          key={item.defaultItem.id}
          defaultItem={item.defaultItem}
          isActive={drawingSettings[item.defaultItem.id]}
          isInAction={item.isInAction}
          disabledTooltip={item.disabledTooltip}
          disabled={item.isDisabled}
          onChange={onDrawingSettingClick}
        />
      ))}
      {availableDrawingActions.map((item) => (
        <ToolbarItem
          key={item.defaultItem.id}
          defaultItem={item.defaultItem}
          isActive={false}
          disabled={item.isDisabled}
          disabledTooltip={item.disabledTooltip}
          onChange={onDrawingActionClick}
        />
      ))}
    </ToolbarComponent>
  )
}
